import { useEffect, useRef } from 'react';
import { useTranslation } from '../hooks';

const History = () => {
  const { t } = useTranslation();
  const cardsRef = useRef([]);

  useEffect(() => {
    const handleScroll = () => {
      cardsRef.current.forEach((card, index) => {
        if (!card) return;

        const rect = card.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        if (rect.top < windowHeight * 0.85) {
          setTimeout(() => {
            card.classList.add('opacity-100', 'translate-y-0');
            card.classList.remove('opacity-0', 'translate-y-8');
          }, index * 150);
        }
      });
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section id="history" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl lg:text-5xl font-light mb-6 tracking-wide text-gray-900">
            {t.history.title}
          </h2>
          <div className="w-24 h-0.5 bg-black mx-auto"></div>
        </div>

        {/* Timeline Container */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-gray-300 via-black to-gray-300 hidden lg:block"></div>

          {/* Timeline Items */}
          <div className="space-y-16 lg:space-y-24">
            {t.history.milestones.map((milestone, index) => {
              const isLeft = index % 2 === 0;

              return (
                <div
                  key={milestone.year}
                  ref={(el) => (cardsRef.current[index] = el)}
                  className={`relative opacity-0 translate-y-8 transition-all duration-700 ${
                    isLeft ? 'lg:pr-1/2' : 'lg:pl-1/2'
                  }`}
                >
                  <div className={`lg:grid lg:grid-cols-2 lg:gap-8 items-center ${
                    isLeft ? '' : 'lg:flex-row-reverse'
                  }`}>
                    {/* Year Badge */}
                    <div className={`hidden lg:block ${
                      isLeft ? 'lg:col-start-2 lg:pl-16' : 'lg:col-start-1 lg:pr-16'
                    }`}>
                      <div className={`flex items-center ${isLeft ? '' : 'lg:justify-end'}`}>
                        {/* Connecting Line */}
                        <div className={`absolute left-1/2 transform -translate-x-1/2 w-12 h-0.5 bg-black ${
                          isLeft ? 'translate-x-6' : '-translate-x-6'
                        }`}></div>

                        {/* Year Circle */}
                        <div className="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-white border-4 border-black rounded-full flex items-center justify-center shadow-lg z-10">
                          <span className="text-sm font-semibold text-black tracking-wider">
                            {milestone.year}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Content Card */}
                    <div className={`${
                      isLeft ? 'lg:col-start-1 lg:pr-16' : 'lg:col-start-2 lg:pl-16'
                    }`}>
                      {/* Mobile Year Badge */}
                      <div className="lg:hidden mb-6 flex items-center">
                        <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center mr-4">
                          <span className="text-xs font-semibold tracking-wider">
                            {milestone.year}
                          </span>
                        </div>
                        <div className="flex-1 h-0.5 bg-gray-300"></div>
                      </div>

                      {/* Card Content */}
                      <div className={`bg-white border-l-4 border-black shadow-lg hover:shadow-xl transition-all duration-300 p-8 group ${
                        isLeft ? 'lg:text-right' : 'lg:text-left'
                      }`}>
                        <h3 className="text-2xl font-light mb-4 text-gray-900 tracking-wide group-hover:text-black transition-colors">
                          {milestone.title}
                        </h3>
                        <p className="text-gray-700 leading-relaxed mb-6">
                          {milestone.description}
                        </p>

                        {/* Partners/Features List */}
                        {milestone.features && milestone.features.length > 0 && (
                          <div className={`mt-6 ${
                            isLeft ? 'lg:flex lg:justify-end' : ''
                          }`}>
                            <ul className={`inline-block space-y-3 ${
                              isLeft ? 'lg:text-right' : 'lg:text-left'
                            }`}>
                              {milestone.features.map((feature, idx) => (
                                <li
                                  key={idx}
                                  className="flex items-center text-sm text-gray-600 hover:text-black transition-colors"
                                >
                                  <div className={`w-2 h-2 bg-black rounded-full ${
                                    isLeft ? 'lg:order-2 lg:ml-3 mr-3' : 'mr-3'
                                  }`}></div>
                                  <span className="font-medium tracking-wide">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom Decoration */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-full">
              <div className="w-8 h-8 border-2 border-white rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default History;
